import { MenuItem, SelectChangeEvent, Stack } from '@mui/material';
import React, { Fragment } from 'react';
import {
  DatePicker,
  SelectField,
  TimePicker,
  TimeRangePicker,
} from '../../../../../components/forms';
import { capitalizeStr } from '../../../../../helpers/stringHelpers';
import { DueDateTimeSectionFormData, IDueDateTimeSection } from './interfaces';
import { useTranslation } from 'react-i18next';

export const DueDateTimeSection: React.FC<IDueDateTimeSection> = ({
  formData,
  formErrors,
  fullScreen = false,
  disablePast = false,
  setFormFieldData,
}): JSX.Element => {
  const { t } = useTranslation();
  const timeTypeOptions: DueDateTimeSectionFormData['timeType'][] = [
    'no-time',
    'exact',
    'range',
  ];

  return (
    <Fragment>
      <DatePicker
        label={t('entities:plannedActivity.performAt')}
        onChange={(date) => setFormFieldData('performAt', date)}
        selectedDate={formData.performAt}
        error={!!formErrors.performAt}
        errorMessage={formErrors.performAt}
        disablePast={disablePast}
        required
      />

      <Stack
        sx={{
          width: fullScreen ? undefined : '400px',
          gap: '10px',
          border:
            formData.timeType !== 'no-time' ? '1px solid #C4C4C4' : undefined,
          padding:
            formData.timeType !== 'no-time' ? '12px 5px 10px' : undefined,
          boxSizing: 'border-box',
          borderRadius: '4px',
        }}
      >
        <SelectField
          name="time-type"
          label={t('forms:createPlannedActivity.fields.timeType.label')}
          value={formData.timeType}
          onChange={(event: SelectChangeEvent) =>
            setFormFieldData(
              'timeType',
              event.target.value as DueDateTimeSectionFormData['timeType']
            )
          }
          required
        >
          {timeTypeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {t(
                `forms:createPlannedActivity.fields.timeType.options.${option}`
              )}
            </MenuItem>
          ))}
        </SelectField>

        {formData.timeType === 'exact' && (
          <TimePicker
            label={capitalizeStr(t('words.time'))}
            selectedTime={formData.startTime}
            onChange={(time) => setFormFieldData('startTime', time)}
            error={!!formErrors.startTime}
            errorMessage={formErrors.startTime}
            required
          />
        )}

        {formData.timeType === 'range' && (
          <TimeRangePicker
            value={{
              from: formData.startTime || null,
              to: formData.endTime || null,
            }}
            onChange={(timeRange) => {
              setFormFieldData('startTime', timeRange.from);
              setFormFieldData('endTime', timeRange.to);
            }}
            error={{
              start: !!formErrors.startTime,
              end: !!formErrors.endTime,
            }}
            errorMessage={{
              start: formErrors.startTime,
              end: formErrors.endTime,
            }}
            required
          />
        )}
      </Stack>
    </Fragment>
  );
};
